import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useNotification } from '../../../contexts/NotificationContext';
import googleAuthService from '../../../utils/googleAuthService';
import googleBusinessService from '../../../utils/googleBusinessService';

/**
 * IntegrationsTab Component
 * Manages third-party platform integrations (Google Business Profile, AI Reply Settings)
 */
const IntegrationsTab = () => {
  const { currentOrg } = useOrganization();
  const { addNotification } = useNotification();

  const [googleConnection, setGoogleConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Reply Settings
  const [replySettings, setReplySettings] = useState({
    auto_reply_enabled: false,
    auto_reply_for_ratings: [4, 5],
    reply_tone: 'professional',
    require_approval: true,
    custom_instructions: '',
    business_context: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (currentOrg?.id) {
      loadData();
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    // Check for OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      addNotification('success', 'Successfully connected to Google Business Profile!');
      // Remove query params
      window.history.replaceState({}, '', window.location.pathname);
      loadData();
    } else if (params.get('google_error')) {
      addNotification('error', `Failed to connect: ${params.get('google_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load Google connection
      const connectionResult = await googleAuthService.getConnection(currentOrg.id);
      if (connectionResult.success && connectionResult.data) {
        setGoogleConnection(connectionResult.data);
      }

      // Load reply settings
      const settingsResult = await googleBusinessService.getReplySettings(currentOrg.id);
      if (settingsResult.success && settingsResult.data) {
        setReplySettings(settingsResult.data);
      }
    } catch (error) {
      console.error('Error loading integration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogle = () => {
    try {
      googleAuthService.initiateOAuthFlow(currentOrg.id);
    } catch (error) {
      addNotification('error', error.message || 'Failed to initiate OAuth flow');
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Google Business Profile? This will stop syncing reviews.')) {
      return;
    }

    setDisconnecting(true);
    try {
      const result = await googleAuthService.disconnect(currentOrg.id);
      if (result.success) {
        setGoogleConnection(null);
        addNotification('success', 'Disconnected from Google Business Profile');
      } else {
        addNotification('error', result.error || 'Failed to disconnect');
      }
    } catch (error) {
      addNotification('error', 'Error disconnecting');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const result = await googleBusinessService.syncReviews(currentOrg.id);
      if (result.success) {
        addNotification('success', result.message || 'Reviews synced successfully');
        loadData(); // Reload to update last sync time
      } else {
        addNotification('error', result.error || 'Failed to sync reviews');
      }
    } catch (error) {
      addNotification('error', 'Error syncing reviews');
    } finally {
      setSyncing(false);
    }
  };

  const handleReplySettingChange = (field, value) => {
    setReplySettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveReplySettings = async () => {
    setSavingSettings(true);
    try {
      const result = await googleBusinessService.updateReplySettings(currentOrg.id, replySettings);
      if (result.success) {
        addNotification('success', 'Reply settings saved successfully');
      } else {
        addNotification('error', result.error || 'Failed to save settings');
      }
    } catch (error) {
      addNotification('error', 'Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleRating = (rating) => {
    const ratings = replySettings.auto_reply_for_ratings || [];
    if (ratings.includes(rating)) {
      handleReplySettingChange('auto_reply_for_ratings', ratings.filter(r => r !== rating));
    } else {
      handleReplySettingChange('auto_reply_for_ratings', [...ratings, rating].sort());
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Google Business Profile Integration */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Globe" size={20} />
              Google Business Profile
            </h3>
            <p className="text-sm text-text-secondary mt-1">
              Connect your Google Business Profile to sync reviews and post AI-powered replies
            </p>
          </div>
        </div>

        {!googleConnection ? (
          // Not Connected State
          <div className="space-y-4">
            <div className="bg-surface rounded-lg border border-border p-4">
              <h4 className="font-medium text-foreground mb-2">What you can do:</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Automatically sync Google reviews into your dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Generate AI-powered replies to reviews</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Post replies directly to Google from your dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Display Google reviews in embeddable widgets</span>
                </li>
              </ul>
            </div>

            <Button
              variant="primary"
              onClick={handleConnectGoogle}
              iconName="Link"
            >
              Connect with Google
            </Button>
          </div>
        ) : (
          // Connected State
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-green-900 mb-1">Connected</h4>
                <div className="text-sm text-green-700 space-y-1">
                  <p><strong>Account:</strong> {googleConnection.account_name}</p>
                  <p><strong>Email:</strong> {googleConnection.account_email}</p>
                  {googleConnection.location_name && (
                    <p><strong>Location:</strong> {googleConnection.location_name}</p>
                  )}
                  {googleConnection.last_sync_at && (
                    <p>
                      <strong>Last synced:</strong>{' '}
                      {new Date(googleConnection.last_sync_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {googleConnection.sync_error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-900 mb-1">Sync Error</h4>
                  <p className="text-sm text-red-700">{googleConnection.sync_error}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="primary"
                onClick={handleSyncNow}
                loading={syncing}
                iconName="RefreshCw"
              >
                Sync Now
              </Button>
              <Button
                variant="outline"
                onClick={handleDisconnect}
                loading={disconnecting}
                iconName="Unlink"
              >
                Disconnect
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* AI Reply Settings */}
      {googleConnection && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Sparkles" size={20} />
              AI Reply Settings
            </h3>
            <p className="text-sm text-text-secondary mt-1">
              Configure how AI generates and posts replies to your Google reviews
            </p>
          </div>

          <div className="space-y-6">
            {/* Enable Auto-Reply */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Enable AI Replies</label>
                <p className="text-sm text-text-secondary mt-1">
                  Automatically generate AI-powered replies for new reviews
                </p>
              </div>
              <button
                onClick={() => handleReplySettingChange('auto_reply_enabled', !replySettings.auto_reply_enabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  replySettings.auto_reply_enabled ? 'bg-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    replySettings.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Auto-reply for ratings */}
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Auto-reply for these star ratings
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => toggleRating(rating)}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 transition-colors ${
                      replySettings.auto_reply_for_ratings?.includes(rating)
                        ? 'border-primary bg-primary text-white'
                        : 'border-border bg-background text-foreground hover:border-primary'
                    }`}
                  >
                    <Icon name="Star" size={16} className="mx-auto" />
                    <span className="text-xs mt-1 block">{rating}★</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Reply Tone */}
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Reply Tone
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['professional', 'friendly', 'casual'].map((tone) => (
                  <button
                    key={tone}
                    onClick={() => handleReplySettingChange('reply_tone', tone)}
                    className={`py-2 px-4 rounded-lg border-2 capitalize transition-colors ${
                      replySettings.reply_tone === tone
                        ? 'border-primary bg-primary text-white'
                        : 'border-border bg-background text-foreground hover:border-primary'
                    }`}
                  >
                    {tone}
                  </button>
                ))}
              </div>
            </div>

            {/* Require Approval */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Require Approval</label>
                <p className="text-sm text-text-secondary mt-1">
                  Review AI-generated replies before they're posted to Google
                </p>
              </div>
              <button
                onClick={() => handleReplySettingChange('require_approval', !replySettings.require_approval)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  replySettings.require_approval ? 'bg-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    replySettings.require_approval ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Custom Instructions */}
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Custom Instructions (Optional)
              </label>
              <textarea
                value={replySettings.custom_instructions || ''}
                onChange={(e) => handleReplySettingChange('custom_instructions', e.target.value)}
                placeholder="e.g., Always mention our 24/7 customer support, Sign off with 'Thank you for choosing us'"
                className="w-full min-h-[100px] p-3 bg-background border border-border rounded-lg text-foreground placeholder-text-muted focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
              />
              <p className="text-xs text-text-muted mt-1">
                Additional guidelines for AI when generating replies
              </p>
            </div>

            {/* Business Context */}
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Business Context (Optional)
              </label>
              <textarea
                value={replySettings.business_context || ''}
                onChange={(e) => handleReplySettingChange('business_context', e.target.value)}
                placeholder="e.g., We specialize in HVAC repair and maintenance, Family-owned business since 1985"
                className="w-full min-h-[100px] p-3 bg-background border border-border rounded-lg text-foreground placeholder-text-muted focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
              />
              <p className="text-xs text-text-muted mt-1">
                Help AI understand your business for more relevant replies
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-border">
              <Button
                variant="primary"
                onClick={handleSaveReplySettings}
                loading={savingSettings}
                iconName="Save"
              >
                Save Reply Settings
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsTab;

